import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getProducts, getCategories, getSecondHandItems } from '../api';
import ProductCard from '../components/common/ProductCard';
import SecondHandCard from '../components/common/SecondHandCard';
import DroneSVG from '../components/common/DroneSVG';
import {
  FiArrowRight, FiShield, FiTruck, FiHeadphones, FiAward, FiPlus,
  FiRefreshCw, FiCheck,
} from 'react-icons/fi';
import './Home.css';
import './secondhand/SecondHand.css';

const TRUST = [
  { icon: <FiShield />, title: 'Genuine Products', text: 'Official DJI warranty on every unit' },
  { icon: <FiTruck />, title: 'Fast Delivery', text: 'Same-day dispatch inside Kathmandu' },
  { icon: <FiHeadphones />, title: 'Expert Support', text: 'Pilots and technicians on call' },
  { icon: <FiAward />, title: 'Authorized Dealer', text: 'DJI certified sales and service' },
];

const CATEGORY_ICONS = ['🚁', '📷', '🎥', '🔋', '🎒', '🛰️'];

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [secondHand, setSecondHand] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getProducts({ page_size: 8, ordering: '-created_at' }),
      getCategories(),
    ]).then(([p, c]) => {
      setFeatured(p.data.results || []);
      setCategories(c.data.results || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Approved second-hand listings — the API only returns live ones here
  useEffect(() => {
    getSecondHandItems({ page_size: 4, ordering: '-created_at' })
      .then(({ data }) => setSecondHand(data.results || []))
      .catch(() => {});
  }, []);

  return (
    <div className="home">
      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className="hero">
        <div className="container hero-inner">
          <div className="hero-copy animate-fade-up">
            <span className="eyebrow">Official DJI Authorized Dealer · Nepal</span>
            <h1 className="hero-title">
              Elevate your<br />
              <span className="text-red">vision.</span>
            </h1>
            <p className="hero-sub">
              Nepal's home for DJI drones, gimbals and cameras. Genuine stock, full warranty,
              and people who actually fly what they sell.
            </p>

            <div className="hero-actions">
              <Link to="/products" className="btn btn-primary btn-lg">
                Shop Now <FiArrowRight />
              </Link>
              <Link to="/second-hand" className="btn btn-outline btn-lg">
                <FiRefreshCw /> Browse Pre-Owned
              </Link>
            </div>

            <ul className="hero-points">
              <li><FiCheck /> Genuine DJI warranty</li>
              <li><FiCheck /> Khalti &amp; Fonepay accepted</li>
              <li><FiCheck /> Free delivery over NPR 50,000</li>
            </ul>
          </div>

          <div className="hero-visual">
            <div className="hero-glow" aria-hidden="true" />
            <DroneSVG />
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ──────────────────────────────────────────────── */}
      <section className="trust">
        <div className="container trust-grid">
          {TRUST.map((t) => (
            <div key={t.title} className="trust-item">
              <span className="trust-icon">{t.icon}</span>
              <div>
                <strong>{t.title}</strong>
                <span>{t.text}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CATEGORIES ───────────────────────────────────────────────── */}
      {categories.length > 0 && (
        <section className="section container">
          <div className="section-head">
            <div>
              <span className="eyebrow">Browse the range</span>
              <h2 className="section-title">Shop by Category</h2>
            </div>
            <Link to="/products" className="btn btn-ghost">View all <FiArrowRight /></Link>
          </div>

          <div className="cat-grid">
            {categories.slice(0, 6).map((cat, i) => (
              <Link key={cat.id} to={`/products?category=${cat.slug}`} className="cat-card">
                <span className="cat-emoji">{CATEGORY_ICONS[i % CATEGORY_ICONS.length]}</span>
                <span className="cat-name">{cat.name}</span>
                <FiArrowRight className="cat-arrow" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ── LATEST PRODUCTS ──────────────────────────────────────────── */}
      <section className="section container">
        <div className="section-head">
          <div>
            <span className="eyebrow">Fresh stock</span>
            <h2 className="section-title">Latest Products</h2>
            <p>New arrivals, ready to fly out of our Kathmandu store.</p>
          </div>
          <Link to="/products" className="btn btn-ghost">View all <FiArrowRight /></Link>
        </div>

        {loading ? (
          <div className="grid-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 340 }} />
            ))}
          </div>
        ) : featured.length === 0 ? (
          <div className="empty-note">No products published yet.</div>
        ) : (
          <div className="grid-4">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* ── SECOND HAND ──────────────────────────────────────────────── */}
      {secondHand.length > 0 && (
        <section className="section container">
          <div className="section-head">
            <div>
              <span className="eyebrow">Certified pre-owned</span>
              <h2 className="section-title">Second Hand Deals</h2>
              <p>Used DJI gear from the community — every listing checked by our team before it goes live.</p>
            </div>
            <div className="section-head-actions">
              <Link to="/second-hand/sell" className="btn btn-outline btn-sm"><FiPlus /> Sell Yours</Link>
              <Link to="/second-hand" className="btn btn-ghost">View all <FiArrowRight /></Link>
            </div>
          </div>

          <div className="grid-4">
            {secondHand.map((item) => <SecondHandCard key={item.id} item={item} />)}
          </div>
        </section>
      )}

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <section className="section container">
        <div className="cta">
          <div className="cta-copy">
            <h2 className="section-title">Not sure which drone fits?</h2>
            <p>
              Tell us what you shoot and where you fly. Our team will match you to the right
              airframe, batteries and accessories — no upselling.
            </p>
          </div>
          <div className="cta-actions">
            <a
              href="https://wa.me/9779705743261"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-lg"
            >
              Chat on WhatsApp
            </a>
            <a href="mailto:info@djistorenepal.com" className="btn btn-outline btn-lg">Email Us</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
